import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { MerchantRules as RulesType } from "../../domain/merchant";
import { getMerchantRules, saveMerchantRules } from "../../services/api/merchant";
import { normalizeError } from "../../services/http/errors";

export default function MerchantRules() {
  const { tenantSlug = "" } = useParams();
  const [rules, setRules] = useState<RulesType | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success" as "success" | "error" | "info", msg: "" });

  useEffect(() => {
    getMerchantRules(tenantSlug).then(setRules).catch(() => setRules(null));
  }, [tenantSlug]);

  const save = async () => {
    if (!rules) return;
    setSaving(true);
    try {
      await saveMerchantRules(tenantSlug, rules);
      setToast({ open: true, severity: "success", msg: "Rules saved successfully." });
    } catch (e) {
      const err = normalizeError(e);
      setToast({ open: true, severity: "error", msg: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (!rules) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} sx={{ mr: 2 }} />
        <Typography color="text.secondary" fontWeight={600}>Loading rules...</Typography>
      </Box>
    );
  }

  const modeColor = (mode: string) => {
    if (mode === "NON_RETURNABLE") return "error.main";
    if (mode === "DAY_LIMIT") return "warning.main";
    return "success.main";
  };

  return (
    <>
      <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>

      <Grid container spacing={2} alignItems="flex-start">
        {/* Automation Core */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Card>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: "primary.light", color: "primary.main" }}>
                <SecurityOutlinedIcon />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>Automation Core</Typography>
                <Typography variant="caption" color="text.secondary">Define global automation rules</Typography>
              </Box>
            </Stack>

            <CardContent>
              <Stack spacing={2.5}>
                {/* Return Window */}
                <Box sx={{ p: 2, borderRadius: 3, bgcolor: "grey.50", border: 1, borderColor: "divider" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="overline" color="text.secondary">Return Window</Typography>
                    <Typography variant="body2" fontWeight={700}>{rules.returnWindowDays} Days</Typography>
                  </Stack>
                  <Slider
                    value={rules.returnWindowDays}
                    min={1}
                    max={60}
                    onChange={(_, v) => setRules({ ...rules, returnWindowDays: v as number })}
                  />
                </Box>

                {/* Auto-Approval Threshold */}
                <Box sx={{ p: 2, borderRadius: 3, bgcolor: "grey.50", border: 1, borderColor: "divider" }}>
                  <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    Auto-Approval Threshold
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    value={rules.autoApprovalThresholdSar}
                    onChange={(e) => setRules({ ...rules, autoApprovalThresholdSar: Number(e.target.value || "0") })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">SAR</InputAdornment>,
                    }}
                  />
                </Box>

                {/* Toggles */}
                <Stack spacing={1}>
                  {[
                    { label: "Accept Store Credit", key: "acceptStoreCredit" as const },
                    { label: "Allow Exchanges", key: "allowExchanges" as const },
                    { label: "Auto-Approve Low Value", key: "autoApproveLowValue" as const },
                  ].map((tog) => (
                    <Stack
                      key={tog.key}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ p: 1.5, borderRadius: 3, border: 1, borderColor: "divider" }}
                    >
                      <Typography variant="body2" fontWeight={700}>{tog.label}</Typography>
                      <Switch
                        checked={rules[tog.key]}
                        onChange={(e) => setRules({ ...rules, [tog.key]: e.target.checked })}
                      />
                    </Stack>
                  ))}
                </Stack>

                <Button variant="contained" size="large" fullWidth onClick={save} disabled={saving}>
                  {saving ? "Saving..." : "Save Rules"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Category Governance */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: "primary.light", color: "primary.main" }}>
                <TuneOutlinedIcon />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>Category Governance</Typography>
                <Typography variant="caption" color="text.secondary">Overrides by category</Typography>
              </Box>
            </Stack>

            <CardContent>
              <Stack spacing={1.5}>
                {rules.categoryOverrides.map((c) => (
                  <Stack
                    key={c.category}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ p: 1.5, borderRadius: 3, border: 1, borderColor: "divider" }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700}>{c.category}</Typography>
                      <Typography variant="caption" fontWeight={600} sx={{ color: modeColor(c.mode) }}>
                        {c.label}
                      </Typography>
                    </Box>
                    <IconButton size="small">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}

                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<AddIcon />}
                  sx={{ borderStyle: "dashed" }}
                  onClick={() => setToast({ open: true, severity: "info", msg: "Override builder comes next." })}
                >
                  Deploy Category Override
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
